import {
  Typography,
  Grid,
  Stack,
  MenuItem,
  Box,
  Snackbar,
  Checkbox,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { StyledTextBoxv2 } from "../../components/ui/TextBox";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { GreenContainedButton } from "../../components/ui/Buttons";
import { PostBankInfomation } from "../../services/auth.services";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { geocodeByAddress } from "react-google-places-autocomplete";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import countryList from "react-select-country-list";
import { IBAN_COUNTRIES } from "../../enum/ibanCountries";
import currencyCodes from "currency-codes";
import {
  validateBankIdentifier,
  validateSwiftBic,
} from "../../utils/validators";
import { REACT_APP_GOOGLE_API_KEY } from "../../utils/constants";

const boxStyle = {
  background: "#FFFFFF",
  p: 3,
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.04)",
  borderRadius: "10px",
};

export default function BankInfo({ updateStep }) {
  const [bankInfo, setBankInfo] = useState({
    accountNumber: {
      value: "",
      error: false,
      message: "Account Number is required",
    },
    accountHolderName: {
      value: "",
      error: false,
      message: "Account Holder Name is required",
    },
    swiftBicNo: {
      value: "",
      error: false,
      message: "SWIFT/BIC Number is required",
    },
    routingNo: {
      value: "",
      error: false,
      message: "This field is required",
    },
    payAccountType: {
      value: "CHECKING",
      error: false,
      message: "Account Type is required",
    },
    bankName: {
      value: "",
      error: false,
      message: "Bank Name is required",
    },
    bankBranchCode: {
      value: "",
      error: false,
      message: "Bank Branch Code is required",
    },
    bankAddress: {
      value: "",
      error: false,
      message: "Bank Address is required",
    },
    bankCity: {
      value: "",
      error: false,
      message: "Bank City is required",
    },
    bankState: {
      value: "",
      error: false,
      message: "Bank State is required",
    },
    zipCode: {
      value: "",
      error: false,
      message: "Zip Code is required",
    },
    country: {
      value: "United States",
      error: false,
      message: "Country is required",
    },
    currency: {
      value: "USD",
      error: false,
      message: "Currency is required",
    },
    businessEncryptedId: {
      value:
        localStorage.getItem("onboardingBusinessEncryptedId") ||
        localStorage.getItem("businessEncryptedId"),
      error: false,
      message: "businessEncryptedId is required",
    },
  });
  const [inputAddress, setInputAddress] = useState(null);
  const [snackBarAlert, setSnackBarAlert] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [useGoogleAutocomplete, setUseGoogleAutocomplete] = useState(true);
  const countryOptions = useMemo(() => countryList().getData(), []);

  const addressAPIKey = REACT_APP_GOOGLE_API_KEY;
  const allCurrencies = currencyCodes.data; // returns array of all ISO 4217 entries

  useEffect(() => {
    if (inputAddress) {
      geocodeByAddress(inputAddress.label).then((place) => {
        let updatedBankInfo = {
          ...bankInfo,
          bankAddress: { value: inputAddress.label },
        }; // Create a copy of userInfo

        for (var i = 0; i < place[0].address_components.length; i++) {
          var addressType = place[0].address_components[i].types[0];

          switch (addressType) {
            case "locality":
              updatedBankInfo.bankCity.value =
                place[0].address_components[i].long_name;
              break;
            case "administrative_area_level_1":
              updatedBankInfo.bankState.value =
                place[0].address_components[i].long_name;
              break;
            case "country":
              updatedBankInfo.country.value =
                place[0].address_components[i].long_name;
              break;
            case "postal_code":
              updatedBankInfo.zipCode.value =
                place[0].address_components[i].long_name;
              break;
            default:
              break;
          }
        }

        setBankInfo(updatedBankInfo); // Update state once after loop
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputAddress]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create a shallow copy to update
    const updatedBankInfo = { ...bankInfo };

    // 1) Normalize all values to trimmed strings to avoid trim() errors
    Object.keys(updatedBankInfo).forEach((key) => {
      const raw = updatedBankInfo[key]?.value;
      const normalized =
        raw == null
          ? ""
          : typeof raw === "string"
          ? raw.trim()
          : String(raw).trim();
      // keep normalized value so later validators use consistent data
      updatedBankInfo[key] = { ...updatedBankInfo[key], value: normalized };
    });

    // 2) Build propertyValues from normalized values (what we'll send)
    const propertyValues = Object.fromEntries(
      Object.entries(updatedBankInfo).map(([k, v]) => [k, v.value])
    );

    // 3) Run validations (required + field-specific)
    for (const key of Object.keys(updatedBankInfo)) {
      const field = updatedBankInfo[key];
      let errorMessage = "";

      // Required check
      if (!field.value) {
        errorMessage = field.message || "This field is required";
      } else {
        // Field-specific checks
        if (key === "swiftBicNo") {
          errorMessage = validateSwiftBic(field.value);
        } else if (key === "routingNo") {
          // pass country from propertyValues (already normalized)
          errorMessage = validateBankIdentifier(
            propertyValues.country,
            field.value
          );
        }
        // add other field-specific validators here if needed:
        // else if (key === 'accountNumber') { ... }
        // else if (key === 'zipCode') { ... }
      }

      updatedBankInfo[key] = {
        ...field,
        error: !!errorMessage,
        message: errorMessage || field.message, // keep original message if no errorMessage
      };
    }

    // 4) Update state once
    setBankInfo(updatedBankInfo);

    // 5) If no errors, submit
    const hasError = Object.values(updatedBankInfo).some(
      (f) => f.error === true
    );

    if (!hasError) {
      try {
        const data = await PostBankInfomation(propertyValues);
        if (data.ok) {
          updateStep(4);
        } else if (data.status === 500) {
          setSnackBarAlert(true);
          setSnackBarMessage("Email already exists");
        } else if (data.status === 400) {
          setSnackBarAlert(true);
          setSnackBarMessage("Please fill all fields");
        } else {
          setSnackBarAlert(true);
          setSnackBarMessage("Something went wrong, please try again");
        }
      } catch (error) {
        setSnackBarAlert(true);
        setSnackBarMessage("Something went wrong, please try again");
        console.error("Error", error);
      }
    } else {
      console.log("Validation errors found");
    }
  };

  const handleChange = (event) => {
    const value = event.target.value;
    const error = validateBankIdentifier(bankInfo.country.value, value);

    setBankInfo({
      ...bankInfo,
      routingNo: {
        value,
        error: !!error,
        message: error,
      },
    });
  };

  const handleSwiftChange = (event) => {
    const value = event.target.value;
    const error = validateSwiftBic(value);

    setBankInfo({
      ...bankInfo,
      swiftBicNo: {
        value,
        error: !!error,
        message: error,
      },
    });
  };

  return (
    <div>
      <br></br>
      <Box sx={boxStyle}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ color: "#093BA7" }}
          alignItems="center"
        >
          <AccountBalanceIcon />
          <Typography variant="h5">Bank Information</Typography>
        </Stack>

        <br></br>
        <Grid container justifyContent="flex-start">
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                Bank Name
              </Typography>
              <StyledTextBoxv2
                fullWidth
                placeholder="Enter full name of your bank"
                value={bankInfo.bankName.value}
                onChange={(event) =>
                  setBankInfo({
                    ...bankInfo,
                    bankName: {
                      ...bankInfo.bankName,
                      error: false,
                      value: event.target.value,
                    },
                  })
                }
              />
              <Typography variant="body3" color="red">
                {bankInfo.bankName.error ? bankInfo.bankName.message : ""}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                Branch Code
              </Typography>
              <StyledTextBoxv2
                fullWidth
                placeholder="Enter your bank branch code"
                value={bankInfo.bankBranchCode.value}
                onChange={(event) =>
                  setBankInfo({
                    ...bankInfo,
                    bankBranchCode: {
                      ...bankInfo.bankBranchCode,
                      error: false,
                      value: event.target.value,
                    },
                  })
                }
              />
              <Typography variant="body3" color="red">
                {bankInfo.bankBranchCode.error
                  ? bankInfo.bankBranchCode.message
                  : ""}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <br></br>
        <Grid container justifyContent="flex-start">
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                Bank Address
              </Typography>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={useGoogleAutocomplete}
                  onClick={() => {
                    setInputAddress(null);
                    setBankInfo({
                      ...bankInfo,
                      bankAddress: {
                        ...bankInfo.bankAddress,
                        value: "",
                      },
                    });
                    setUseGoogleAutocomplete(!useGoogleAutocomplete);
                  }}
                />
                <Typography variant="body2_light">
                  Use Google Autocomplete
                </Typography>
              </Stack>
              {addressAPIKey && useGoogleAutocomplete ? (
                <GooglePlacesAutocomplete
                  apiKey="AIzaSyD9xcEWBs-_x0-nLofudKB0sT8UeI51Wi0"
                  selectProps={{
                    inputAddress,
                    onChange: setInputAddress,
                    placeholder: "Search your banks address",
                    styles: {
                      control: (provided) => ({
                        ...provided,
                        border: "none",
                        borderRadius: "4px",
                        height: "2.5em",
                        background: "#F9F9FB",
                        color: "#28282A",
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: "#c2c2c4",
                      }),
                    },
                  }}
                />
              ) : (
                <StyledTextBoxv2
                  fullWidth
                  value={bankInfo.bankAddress.value}
                  placeholder="Enter your banks address"
                  onChange={(event) =>
                    setBankInfo({
                      ...bankInfo,
                      bankAddress: {
                        ...bankInfo.bankAddress,
                        error: false,
                        value: event.target.value,
                      },
                    })
                  }
                />
              )}
              <Typography variant="body3" color="red">
                {bankInfo.bankAddress.error ? bankInfo.bankAddress.message : ""}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                City / Town
              </Typography>
              <StyledTextBoxv2
                fullWidth
                placeholder="Enter your banks city or town"
                value={bankInfo.bankCity.value}
                onChange={(event) =>
                  setBankInfo({
                    ...bankInfo,
                    bankCity: {
                      ...bankInfo.bankCity,
                      error: false,
                      value: event.target.value,
                    },
                  })
                }
              />
              <Typography variant="body3" color="red">
                {bankInfo.bankCity.error && bankInfo.bankCity.value === ""
                  ? bankInfo.bankCity.message
                  : ""}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <br></br>
        <Grid container justifyContent="flex-start">
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                State / Province / Region
              </Typography>
              <StyledTextBoxv2
                fullWidth
                placeholder="Enter your banks state, province, or region"
                value={bankInfo.bankState.value}
                onChange={(event) =>
                  setBankInfo({
                    ...bankInfo,
                    bankState: {
                      ...bankInfo.bankState,
                      error: false,
                      value: event.target.value,
                    },
                  })
                }
              />
              <Typography variant="body3" color="red">
                {bankInfo.bankState.error && bankInfo.bankState.value === ""
                  ? bankInfo.bankState.message
                  : ""}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                Postal / ZIP Code
              </Typography>
              <StyledTextBoxv2
                fullWidth
                placeholder="Enter your banks Postal or ZIP Code"
                value={bankInfo.zipCode.value}
                onChange={(event) =>
                  setBankInfo({
                    ...bankInfo,
                    zipCode: {
                      ...bankInfo.zipCode,
                      error: false,
                      value: event.target.value,
                    },
                  })
                }
              />
              <Typography variant="body3" color="red">
                {bankInfo.zipCode.error && bankInfo.zipCode.value === ""
                  ? bankInfo.zipCode.message
                  : ""}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <br></br>
        <Grid container justifyContent="flex-start">
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                Country
              </Typography>
              <StyledTextBoxv2
                fullWidth
                value={bankInfo.country.value}
                select
                onChange={(event) => {
                  setBankInfo({
                    ...bankInfo,
                    country: {
                      ...bankInfo.country,
                      error: false,
                      value: event.target.value,
                    },
                    routingNo: {
                      value: "",
                    },
                  });
                }}
              >
                {countryOptions.map((data, index) => (
                  <MenuItem value={data.label} key={index}>
                    {data.label}
                  </MenuItem>
                ))}
              </StyledTextBoxv2>
              <Typography variant="body3" color="red">
                {bankInfo.country.error ? bankInfo.country.message : ""}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                SWIFT / BIC Number
              </Typography>
              <StyledTextBoxv2
                fullWidth
                placeholder="Enter your bank's SWIFT or BIC code (8 or 11 characters)"
                value={bankInfo.swiftBicNo.value}
                onChange={handleSwiftChange}
              />
              <Typography variant="body3" color="red">
                {bankInfo.swiftBicNo.error ? bankInfo.swiftBicNo.message : ""}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <br></br>
      <Box sx={boxStyle}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ color: "#093BA7" }}
          alignItems="center"
        >
          <FolderSharedIcon />
          <Typography variant="h5">Account Information</Typography>
        </Stack>
        <br></br>
        <Grid container justifyContent="flex-start">
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                Account Holder Name
              </Typography>
              <StyledTextBoxv2
                fullWidth
                placeholder="Enter your full name as it appears on the bank account"
                value={bankInfo.accountHolderName.value}
                onChange={(event) =>
                  setBankInfo({
                    ...bankInfo,
                    accountHolderName: {
                      ...bankInfo.accountHolderName,
                      error: false,
                      value: event.target.value,
                    },
                  })
                }
              />
              <Typography variant="body3" color="red">
                {bankInfo.accountHolderName.error
                  ? bankInfo.accountHolderName.message
                  : ""}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                Account Number
              </Typography>
              <StyledTextBoxv2
                fullWidth
                placeholder="Enter your full bank account number"
                value={bankInfo.accountNumber.value}
                onChange={(event) => {
                  const inputValue = event.target.value;

                  // Allow only digits
                  if (/^\d*$/.test(inputValue)) {
                    setBankInfo({
                      ...bankInfo,
                      accountNumber: {
                        ...bankInfo.accountNumber,
                        error: false,
                        value: inputValue,
                      },
                    });
                  }
                }}
              />
              <Typography variant="body3" color="red">
                {bankInfo.accountNumber.error
                  ? bankInfo.accountNumber.message
                  : ""}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <br></br>
        <Grid container justifyContent="flex-start">
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                Account Type
              </Typography>
              <StyledTextBoxv2
                fullWidth
                value={bankInfo.payAccountType.value}
                select
                onChange={(event) =>
                  setBankInfo({
                    ...bankInfo,
                    payAccountType: {
                      ...bankInfo.payAccountType,
                      error: false,
                      value: event.target.value,
                    },
                  })
                }
              >
                <MenuItem value="SAVINGS">Savings</MenuItem>
                <MenuItem value="CHECKING">Checking/Current</MenuItem>
              </StyledTextBoxv2>
              <Typography variant="body3" color="red">
                {bankInfo.payAccountType.error
                  ? bankInfo.payAccountType.message
                  : ""}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={1} />
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                {IBAN_COUNTRIES.includes(bankInfo.country.value)
                  ? "IBAN (International Bank Account Number)"
                  : bankInfo.country.value === "United States"
                  ? "Routing Number"
                  : bankInfo.country.value === "United Kingdom"
                  ? "Sort Code"
                  : bankInfo.country.value === "Australia"
                  ? "BSB Code"
                  : bankInfo.country.value === "Canada"
                  ? "Transit Number"
                  : "Local Bank Identifier Name"}
              </Typography>
              <StyledTextBoxv2
                fullWidth
                placeholder={
                  IBAN_COUNTRIES.includes(bankInfo.country.value)
                    ? "Enter your IBAN"
                    : bankInfo.country.value === "United States"
                    ? "Enter your 9-digit ABA routing number"
                    : bankInfo.country.value === "United Kingdom"
                    ? "Enter your 6-digit sort code"
                    : bankInfo.country.value === "Australia"
                    ? "Enter your 6-digits BSB number"
                    : bankInfo.country.value === "Canada"
                    ? "Enter your 5-digits transit number"
                    : "Enter your Local Bank Identifier Name"
                }
                value={bankInfo.routingNo.value}
                onChange={handleChange}
              />
              <Typography variant="body3" color="red">
                {bankInfo.routingNo.error ? bankInfo.routingNo.message : ""}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <br></br>
        <Grid container justifyContent="flex-start">
          <Grid item xs={5} container direction="row" alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="neutral.letter_Black">
                Currency
              </Typography>
              <StyledTextBoxv2
                fullWidth
                value={bankInfo.currency.value}
                select
                onChange={(event) =>
                  setBankInfo({
                    ...bankInfo,
                    currency: {
                      ...bankInfo.currency,
                      error: false,
                      value: event.target.value,
                    },
                  })
                }
              >
                {allCurrencies.map((data, index) => (
                  <MenuItem value={data.code} key={index}>
                    {data.code}
                  </MenuItem>
                ))}
              </StyledTextBoxv2>
              <Typography variant="body3" color="red">
                {bankInfo.currency.error ? bankInfo.currency.message : ""}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={1} />
        </Grid>
      </Box>
      <br></br>
      <Stack justifyContent="flex-end" alignItems="flex-end">
        <GreenContainedButton
          variant="contained"
          onClick={(e) => handleSubmit(e)}
          endIcon={<ArrowForwardIosIcon />}
        >
          Next
        </GreenContainedButton>
      </Stack>
      <br></br>
      <Snackbar
        open={snackBarAlert}
        onClose={() => setSnackBarAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ top: "0 !important", left: "0 !important" }}
        autoHideDuration={6000}
      >
        <Box
          onClose={() => setSnackBarAlert(false)}
          sx={{ width: "100vw", background: "#F33535", p: 0.5 }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{
              width: "100%",
              textAlign: "center",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
            }}
          >
            <CancelOutlinedIcon fontSize="small" />
            <Typography variant="body2">{snackBarMessage}</Typography>
          </Stack>
        </Box>
      </Snackbar>
      <br></br>
    </div>
  );
}
